import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaHandsHelping,
  FaHeart,
  FaGlobeAfrica,
} from "react-icons/fa";

const AboutSection = styled.section`
  padding: 6rem 0;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }

  @media (max-width: 480px) {
    padding: 3rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 1rem;

  @media (max-width: 1024px) {
    font-size: 2.5rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.2rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto 4rem auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (max-width: 1024px) {
    gap: 3rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    gap: 1.5rem;
    margin-bottom: 2rem;
  }
`;

const AboutText = styled.div`
  h3 {
    font-size: 2rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
      font-size: 1.8rem;
      text-align: center;
    }

    @media (max-width: 480px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #666;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
      font-size: 1rem;
      text-align: center;
    }

    @media (max-width: 480px) {
      font-size: 0.95rem;
      line-height: 1.6;
    }
  }
`;

const AboutImage = styled.div`
  background: linear-gradient(135deg, #005d3b 0%, #002b16 100%);
  height: 400px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 4rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="water" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="2" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23water)"/></svg>');
    opacity: 0.3;
  }

  @media (max-width: 1024px) {
    height: 350px;
    font-size: 3.5rem;
  }

  @media (max-width: 768px) {
    height: 300px;
    font-size: 3rem;
  }

  @media (max-width: 480px) {
    height: 250px;
    font-size: 2.5rem;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 4rem;
`;

const ValueCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const ValueIcon = styled.div`
  font-size: 3rem;
  color: #005d3b;
  margin-bottom: 1rem;
`;

const ValueTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
`;

const ValueDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const About = () => {
  return (
    <AboutSection id="about">
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Who We Are
        </SectionTitle>

        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          A digital solution built to give Africans the tools and confidence to
          shape accountable communities
        </SectionSubtitle>

        <AboutContent>
          <AboutText>
            <motion.h3
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Our Purpose
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Our purpose is to empower Africans to make a difference where they
              live. WATA is about turning conversations into action, and voices
              into change. We believe that progress begins when everyday people
              feel empowered to act.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              We use digital solutions to impact lives in Africa. Every feature,
              update, and message exists to make participation in accountability
              and community-building simpler and more engaging.
            </motion.p>
          </AboutText>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <AboutImage>
              <FaGlobeAfrica />
            </AboutImage>
          </motion.div>
        </AboutContent>

        <ValuesGrid>
          <ValueCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <ValueIcon>
              <FaHandsHelping />
            </ValueIcon>
            <ValueTitle>Growth</ValueTitle>
            <ValueDescription>
              We push forward and help communities grow stronger.
            </ValueDescription>
          </ValueCard>

          <ValueCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <ValueIcon>
              <FaHeart />
            </ValueIcon>
            <ValueTitle>Reliability</ValueTitle>
            <ValueDescription>
              We stand firm and dependable in both function and message.
            </ValueDescription>
          </ValueCard>

          <ValueCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <ValueIcon>
              <FaUsers />
            </ValueIcon>
            <ValueTitle>Accountability</ValueTitle>
            <ValueDescription>
              We believe in openness and responsibility.
            </ValueDescription>
          </ValueCard>

          <ValueCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <ValueIcon>
              <FaHandsHelping />
            </ValueIcon>
            <ValueTitle>Community</ValueTitle>
            <ValueDescription>
              We thrive together and act together.
            </ValueDescription>
          </ValueCard>
        </ValuesGrid>
      </Container>
    </AboutSection>
  );
};

export default About;
